import React, { useState, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { FaArrowLeft, FaShieldAlt } from 'react-icons/fa';
import { iRootState, iRootDispatch } from '../../store';
import { save as saveBondInsurance } from '../../services/bond-insurance';
import { AppModal } from '../../components/Modal';
import {
  PageContainer,
  Header,
  BackButton,
  HeaderTitle,
  Content,
  LoadingOverlay,
  LoadingSpinner,
  LoadingText,
} from './styles';
import { MobileAnalysisFormData, initialFormData } from './types';
import StepIndicator from './components/common/StepIndicator';
import Step1_TenantType from './components/steps/Step1_TenantType';
import Step2_TenantData from './components/steps/Step2_TenantData';
import Step3_PropertyAddress from './components/steps/Step3_PropertyAddress';
import Step4_PropertyValues from './components/steps/Step4_PropertyValues';
import Step5_Review from './components/steps/Step5_Review';

const TOTAL_STEPS = 5;

const mapState = (state: iRootState) => ({
  user: state.user,
});

const mapDispatch = (dispatch: iRootDispatch) => ({});

type Props = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch> & RouteComponentProps;

const PwaNewAnalysis: React.FC<Props> = ({ user, history }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<MobileAnalysisFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [createdBondInsuranceId, setCreatedBondInsuranceId] = useState('');

  const handleModalClose = useCallback(() => {
    setModalIsOpen(false);
    history.push('/admin/bond-insurances');
  }, [history]);

  const updateFormData = useCallback((updates: Partial<MobileAnalysisFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const goToNextStep = useCallback(() => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      history.goBack();
    }
  }, [currentStep, history]);

  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= TOTAL_STEPS) {
      setCurrentStep(step);
    }
  }, []);

  const parseMoneyValue = (value: string): number => {
    if (!value) return 0;
    return parseFloat(value.replace(/[R$\s.]/g, '').replace(',', '.')) || 0;
  };

  // unmaskMoney() do serviço usa parseFloat() que não suporta o prefixo "R$ ".
  // Esta função retorna apenas o valor numérico formatado (ex: "1.500,00") sem o símbolo.
  const stripCurrencySymbol = (value: string): string =>
    (value || '0,00').replace('R$', '').trim();

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const isNaturalPerson = formData.tenantType === 'NATURAL';
      const nt = formData.naturalTenant;
      const lt = formData.legalTenant;

      const rentValue = parseMoneyValue(formData.values.rentValue);
      const condominiumFee = formData.values.isCondominiumEnabled ? parseMoneyValue(formData.values.condominiumFee) : 0;
      const taxesValue = formData.values.isTaxesEnabled ? parseMoneyValue(formData.values.taxesValue) : 0;
      const energyBill = formData.values.isEnergyEnabled ? parseMoneyValue(formData.values.energyBill) : 0;
      const waterBill = formData.values.isWaterEnabled ? parseMoneyValue(formData.values.waterBill) : 0;
      const gasBill = formData.values.isGasEnabled ? parseMoneyValue(formData.values.gasBill) : 0;
      const totalAmount = rentValue + condominiumFee + taxesValue + energyBill + waterBill + gasBill;

      const payload: any = {
        initial_info: {
          estate: { id: formData.estateId },
          purpose: formData.purpose,
          tenant_type: formData.tenantType,
          analysis_assurance: 'COMPLETE',
        },
        property_info: {
          zip_code: formData.property.zipCode,
          street: formData.property.street,
          number: formData.property.number,
          complement: formData.property.complement,
          district: formData.property.district,
          city: formData.property.city,
          state: formData.property.state,
          property_type: formData.property.propertyType,
          reason: formData.property.reason,
          rent_value: stripCurrencySymbol(formData.values.rentValue),
          condominium_fee: formData.values.isCondominiumEnabled ? stripCurrencySymbol(formData.values.condominiumFee) : '0,00',
          taxes_value: formData.values.isTaxesEnabled ? stripCurrencySymbol(formData.values.taxesValue) : '0,00',
          energy_bill: formData.values.isEnergyEnabled ? stripCurrencySymbol(formData.values.energyBill) : '0,00',
          water_bill: formData.values.isWaterEnabled ? stripCurrencySymbol(formData.values.waterBill) : '0,00',
          gas_bill: formData.values.isGasEnabled ? stripCurrencySymbol(formData.values.gasBill) : '0,00',
          contract_penalties_value: '0,00',
          interior_painting_value: '0,00',
          exterior_painting_value: '0,00',
          furniture_damage_value: '0,00',
          property_damage_value: '0,00',
          total_amount: totalAmount,
          validity: formData.values.validity,
        },
        tenant_info: {},
        owner_info: {},
        attachments: [],
      };

      if (isNaturalPerson) {
        payload.tenant_info = {
          main_tenant_cpf: nt.cpf.replace(/\D/g, ''),
          main_tenant_name: nt.name,
          main_tenant_email: nt.email,
          main_tenant_phone: nt.phone.replace(/\D/g, ''),
          main_tenant_birth_date: nt.birthDate,
          main_tenant_gender: nt.gender,
          main_tenant_mother_name: nt.motherName || 'Não informado',
          main_tenant_nationality: nt.nationality,
          main_tenant_marital_status: nt.maritalStatus,
          main_tenant_social_name: nt.hasSocialName ? nt.socialName : undefined,
          main_tenant_politically_exposed_person: nt.politicallyExposedPerson,
          ...(nt.politicallyExposedPerson === 'CLOSE_TO' && {
            main_tenant_close_to_politically_exposed_person: {
              politically_exposed_person_name: nt.pepName,
              politically_exposed_person_cpf: nt.pepCpf.replace(/\D/g, ''),
              degree_of_kinship_to_politically_exposed_person: nt.pepKinship,
            },
          }),
          employment_relationship: nt.employmentRelationship,
          employment_admission_date: nt.employmentAdmissionDate || undefined,
          main_tenant_profession: nt.profession,
          main_tenant_rg_emission_date: new Date().toISOString().split('T')[0],
          resident: nt.isResident,
          // Resident info (when tenant is NOT the resident)
          ...(!nt.isResident && {
            resident_cpf: nt.residentCpf.replace(/\D/g, ''),
            resident_name: nt.residentName,
            resident_gender: nt.residentGender,
            resident_birth_date: nt.residentBirthDate,
            resident_mother_name: nt.residentMotherName,
            resident_email: nt.residentEmail,
            resident_phone: nt.residentPhone.replace(/\D/g, ''),
            resident_marital_status: nt.residentMaritalStatus,
            resident_nationality: nt.residentNationality,
            resident_politically_exposed_person: nt.residentPoliticallyExposedPerson,
            ...(nt.residentPoliticallyExposedPerson === 'CLOSE_TO' && {
              resident_close_to_politically_exposed_person: {
                politically_exposed_person_name: nt.residentPepName,
                politically_exposed_person_cpf: nt.residentPepCpf.replace(/\D/g, ''),
                degree_of_kinship_to_politically_exposed_person: nt.residentPepKinship,
              },
            }),
            resident_employment_relationship: nt.residentEmploymentRelationship,
            resident_profession: nt.residentProfession,
          }),
          // Marital partner
          partner_cpf: nt.partnerCpf ? nt.partnerCpf.replace(/\D/g, '') : '',
          partner_name: nt.partnerName || '',
          rent: 0,
          partner_rent: 0,
          resident_monthly_fixed_income: 0,
          resident_other_incomes: 0,
          // Sympathise tenants
          include_sympathise_tenants: nt.includeSympathiseTenants,
          sympathise_tenants: nt.includeSympathiseTenants
            ? nt.sympathiseTenants.map((t) => ({
              cpf: t.cpf.replace(/\D/g, ''),
              name: t.name,
              gender: t.gender,
              birth_date: t.birthDate,
              mother_name: t.motherName,
              email: t.email,
              phone: t.phone.replace(/\D/g, ''),
              marital_status: t.maritalStatus,
              nationality: t.nationality,
              resident: t.resident,
              politically_exposed_person: t.politicallyExposedPerson,
              ...(t.politicallyExposedPerson === 'CLOSE_TO' && {
                close_to_politically_exposed_person: {
                  politically_exposed_person_name: t.pepName,
                  politically_exposed_person_cpf: t.pepCpf.replace(/\D/g, ''),
                  degree_of_kinship_to_politically_exposed_person: t.pepKinship,
                },
              }),
              employment_relationship: t.employmentRelationship,
              profession: t.profession,
              monthly_fixed_income: parseMoneyValue(t.monthlyFixedIncome),
              other_incomes: parseMoneyValue(t.otherIncomes),
            }))
            : [],
        };
      } else {
        payload.tenant_info = {
          document: lt.cnpj.replace(/\D/g, ''),
          company: lt.companyName,
          foundation_date: lt.foundationDate,
          last_contractual_alteration_date: lt.lastContractualAlterationDate || undefined,
          share_capital: lt.shareCapital,
          paid_in_capital: lt.paidInCapital || undefined,
          anual_revenue: lt.anualRevenue || undefined,
          email: lt.email,
          phone: lt.phone.replace(/\D/g, ''),
          contact_name: lt.contactName,
          contact_phone: lt.contactPhone?.replace(/\D/g, '') || '',
          partners: lt.partners.map((p) => ({
            document: p.cpf.replace(/\D/g, ''),
            name: p.name,
            percent: parseFloat(p.percentCapital) || 0,
            date_of_entry: p.dateOfEntry,
          })),
        };
      }

      const response = await saveBondInsurance(payload, user.id, user.broker?.id || '');

      if (response && response.id) {
        setCreatedBondInsuranceId(response.id);
      }
      setModalIsOpen(true);
    } catch (error) {
      console.error('Submit error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erro ao enviar análise. Tente novamente.';
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1_TenantType
            formData={formData}
            updateFormData={updateFormData}
            onNext={goToNextStep}
            userId={user.id}
            userEstateId={user.estate?.id}
            userEstateName={user.estate?.name}
            brokerId={user.broker?.id}
          />
        );
      case 2:
        return (
          <Step2_TenantData
            formData={formData}
            updateFormData={updateFormData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 3:
        return (
          <Step3_PropertyAddress
            formData={formData}
            updateFormData={updateFormData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
            userId={user.id}
          />
        );
      case 4:
        return (
          <Step4_PropertyValues
            formData={formData}
            updateFormData={updateFormData}
            onNext={goToNextStep}
            onBack={goToPreviousStep}
          />
        );
      case 5:
        return (
          <Step5_Review
            formData={formData}
            onSubmit={handleSubmit}
            onBack={goToPreviousStep}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={goToPreviousStep}>
          <FaArrowLeft size={20} />
        </BackButton>
        <HeaderTitle>
          <FaShieldAlt style={{ marginRight: 8 }} />
          Nova Análise
        </HeaderTitle>
        <div style={{ width: 36 }} />
      </Header>

      <Content>
        <StepIndicator currentStep={currentStep} totalSteps={TOTAL_STEPS} />
        {renderStep()}
      </Content>

      {isSubmitting && (
        <LoadingOverlay>
          <LoadingSpinner style={{ width: 40, height: 40 }} />
          <LoadingText>Enviando análise...</LoadingText>
        </LoadingOverlay>
      )}

      <AppModal
        message={'Seus dados foram enviados para análise'}
        success={true}
        modalIsOpen={modalIsOpen}
        handleClose={handleModalClose}
        trigger={<></>}
        bondInsuranceId={createdBondInsuranceId}
      />
    </PageContainer>
  );
};

export default connect(mapState, mapDispatch)(PwaNewAnalysis);
